// src/features/october/components/ProductGrid.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from './ProductCard';
import { Link } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const ProductGrid = ({ 
  products = [], 
  loading = false, 
  error = null,
  emptyMessage = "No products found",
  emptyDescription = "Try adjusting your filters to see more products.",
  className = "",
  viewMode = "grid"
}) => {
  const [columns, setColumns] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [mobileViewMode, setMobileViewMode] = useState('carousel');
  const carouselRef = useRef(null);
  const { addToCart, isInCart, getItemQuantity } = useCart();

  // Detect mobile and update columns
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      setIsMobile(mobile);
      if (width < 640) {
        setColumns(2);  // Mobile: 2 columns for better product visibility
      } else if (width < 768) {
        setColumns(2);  // Small tablet: 2 columns
      } else if (width < 1024) {
        setColumns(3);  // Tablet: 3 columns
      } else if (width < 1280) {
        setColumns(4);  // Desktop: 4 columns
      } else if (width < 1536) {
        setColumns(4);  // Large desktop: 4 columns
      } else {
        setColumns(5);  // XL screens: 5 columns
      }
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  useEffect(() => {
    if (mobileViewMode === 'carousel') {
      setCarouselIndex(0);
    }
  }, [mobileViewMode]);

  // Touch/swipe handlers for mobile carousel
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && carouselIndex < Math.ceil(products.length / 2) - 1) {
      setCarouselIndex(carouselIndex + 1);
    }
    if (isRightSwipe && carouselIndex > 0) {
      setCarouselIndex(carouselIndex - 1);
    }
  };

  // Enhanced add to cart with luxury feedback
  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  // Sophisticated loading skeleton
  const LoadingSkeleton = ({ index }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        delay: index * 0.1,
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      className="bg-brand-black/80 rounded-3xl overflow-hidden border border-brand-red/20 shadow-[0_25px_60px_-30px_rgba(255,195,0,0.35)]"
    >
      {/* Image skeleton with shimmer */}
      <div className="relative h-80 bg-gradient-to-br from-brand-black/80 via-brand-black/70 to-brand-black/60 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent animate-pulse" />
      </div>
      
      {/* Content skeleton */}
      <div className="p-6 text-center space-y-4">
        <div className="h-4 bg-brand-gold/10 rounded-lg w-3/4 mx-auto" />
        <div className="h-5 bg-brand-gold/10 rounded-lg w-1/2 mx-auto" />
      </div>
    </motion.div>
  );

  // Luxury error state
  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`text-center py-24 ${className}`}
      >
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-brand-red/15 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-brand-red/30 shadow-[0_20px_50px_-20px_rgba(230,0,0,0.45)]">
            <RefreshCw className="w-10 h-10 text-brand-red" />
          </div>
          <h3 className="text-2xl font-heading text-brand-gold mb-4 tracking-wide">
            Something went wrong
          </h3>
          <p className="text-brand-light/70 font-body mb-8 leading-relaxed">
            {error}
          </p>
          <motion.button 
            onClick={() => window.location.reload()}
            className="px-8 py-3 rounded-2xl bg-brand-red text-brand-light hover:bg-brand-red/80 transition-all duration-300 font-medium tracking-wide shadow-[0_20px_45px_-20px_rgba(230,0,0,0.6)]"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Try Again
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // Luxury loading state
  if (loading) {
    return (
      <div className={className}>
        <div className={`grid gap-8 ${
          columns === 1 ? 'grid-cols-1' :
          columns === 2 ? 'grid-cols-2' :
          columns === 3 ? 'grid-cols-3' :
          columns === 4 ? 'grid-cols-4' :
          'grid-cols-5'
        }`}>
          {Array.from({ length: 12 }).map((_, index) => (
            <LoadingSkeleton key={index} index={index} />
          ))}
        </div>
      </div>
    );
  }

  // Luxury empty state
  if (products.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={`text-center py-32 ${className}`}
      >
        <div className="max-w-lg mx-auto">
          <div className="w-24 h-24 bg-brand-black border border-brand-gold/30 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-[0_25px_60px_-30px_rgba(255,195,0,0.35)]">
            <Search className="w-12 h-12 text-brand-gold" strokeWidth={1.5} />
          </div>
          <h3 className="text-2xl font-heading text-brand-gold mb-4 tracking-wide">
            {emptyMessage}
          </h3>
          <p className="text-brand-light/70 font-body leading-relaxed text-lg">
            {emptyDescription}
          </p>
        </div>
      </motion.div>
    );
  }

  // List view - Luxury minimal design
  if (viewMode === "list") {
    return (
      <div className={`space-y-6 ${className}`}>
        <AnimatePresence mode="popLayout">
          {products.map((product, index) => {
            const inCart = isInCart(product.id);
            const cartQuantity = getItemQuantity(product.id);
            
            return (
              <motion.div
                key={product.id}
                layout
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ 
                  delay: index * 0.05,
                  duration: 0.6,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="group bg-brand-black/80 rounded-3xl border border-brand-red/20 overflow-hidden hover:-translate-y-1 hover:border-brand-gold/40 transition-all duration-500 shadow-[0_25px_60px_-30px_rgba(255,195,0,0.35)]"
              >
                <div className="flex">
                  <Link to={`/kganya-scents/product/${product.id}`} className="w-32 h-32 flex-shrink-0 overflow-hidden bg-brand-black">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </Link>
                  <div className="flex-1 p-6 flex items-center justify-between">
                    <div className="flex-1">
                      <Link to={`/kganya-scents/product/${product.id}`}>
                        <h3 className="font-heading text-brand-gold text-lg mb-2 transition-colors tracking-wide group-hover:text-brand-red">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-base font-body text-brand-light/70 tracking-wider">
                        {product.price}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {inCart ? (
                        <motion.div 
                          className="flex items-center gap-3 bg-brand-red/20 text-brand-gold px-6 py-3 rounded-2xl font-medium tracking-wide border border-brand-red/30"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 200 }}
                        >
                          <div className="w-2 h-2 bg-brand-red rounded-full animate-pulse" />
                          <span>In Cart ({cartQuantity})</span>
                        </motion.div>
                      ) : (
                        <motion.button 
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={!product.inStock}
                          className={`px-6 py-3 rounded-2xl font-medium tracking-wide transition-all duration-300 ${
                            product.inStock
                              ? 'bg-brand-red text-brand-light hover:bg-brand-red/80 shadow-[0_20px_45px_-20px_rgba(230,0,0,0.6)]'
                              : 'bg-brand-black/40 text-brand-light/30 cursor-not-allowed'
                          }`}
                          whileHover={{ scale: product.inStock ? 1.02 : 1 }}
                          whileTap={{ scale: product.inStock ? 0.98 : 1 }}
                        >
                          {product.inStock ? 'Add to Cart' : 'Sold Out'}
                        </motion.button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    );
  }

  // Grid view - Mobile Carousel / Desktop Grid
  const createColumns = () => {
    const cols = Array.from({ length: columns }, () => []);
    products.forEach((product, index) => {
      cols[index % columns].push({ ...product, index });
    });
    return cols;
  };

  const gridColumns = createColumns();
  const totalCarouselPages = Math.ceil(products.length / 2);

  // Mobile carousel view
  if (isMobile && viewMode === "grid") {
    return (
      <div className={className}>
        <div className="mb-4 flex rounded-full bg-brand-black border border-brand-red/25 p-1 text-xs font-medium text-brand-light/70">
          {[
            { id: 'carousel', label: 'Curated view' },
            { id: 'grid', label: 'Show all' },
          ].map((mode) => (
            <button
              key={mode.id}
              className={`flex-1 rounded-full px-4 py-2 transition-all ${
                mobileViewMode === mode.id
                  ? 'bg-brand-red text-brand-light shadow-[0_15px_45px_-25px_rgba(230,0,0,0.6)]'
                  : 'text-brand-light/60 hover:text-brand-light'
              }`}
              onClick={() => setMobileViewMode(mode.id)}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {mobileViewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-4">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        ) : (
        <div className="relative">
          {/* Carousel Container */}
          <div
            ref={carouselRef}
            className="overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
            >
              {Array.from({ length: totalCarouselPages }).map((_, pageIndex) => {
                const pageProducts = products.slice(pageIndex * 2, (pageIndex + 1) * 2);
                return (
                  <div key={pageIndex} className="min-w-full px-2">
                    <div className="grid grid-cols-2 gap-4">
                      {pageProducts.map((product, idx) => (
                        <ProductCard 
                          key={product.id} 
                          product={product} 
                          index={pageIndex * 2 + idx}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carousel Navigation */}
          {totalCarouselPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                disabled={carouselIndex === 0}
                className={`p-2 rounded-xl border transition-colors touch-manipulation ${
                  carouselIndex === 0
                    ? 'border-brand-gold/20 text-brand-light/30 cursor-not-allowed'
                    : 'border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10'
                }`}
                aria-label="Previous products"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Carousel Indicators */}
              <div className="flex gap-2">
                {Array.from({ length: totalCarouselPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 touch-manipulation ${
                      carouselIndex === index
                        ? 'bg-brand-red w-8'
                        : 'bg-brand-red/30 w-2'
                    }`}
                    aria-label={`Go to page ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCarouselIndex(Math.min(totalCarouselPages - 1, carouselIndex + 1))}
                disabled={carouselIndex === totalCarouselPages - 1}
                className={`p-2 rounded-xl border transition-colors touch-manipulation ${
                  carouselIndex === totalCarouselPages - 1
                    ? 'border-brand-gold/20 text-brand-light/30 cursor-not-allowed'
                    : 'border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10'
                }`}
                aria-label="Next products"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        )}
      </div>
    );
  }

  // Desktop grid view
  return (
    <div className={className}>
      <div className={`grid gap-8 ${
        columns === 1 ? 'grid-cols-1' :
        columns === 2 ? 'grid-cols-2' :
        columns === 3 ? 'grid-cols-3' :
        columns === 4 ? 'grid-cols-4' :
        'grid-cols-5'
      }`}>
        {gridColumns.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-col gap-8">
            <AnimatePresence mode="popLayout">
              {column.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  index={product.index}
                />
              ))}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// Enhanced export variants for different luxury use cases
export const FeaturedProductGrid = ({ products, loading, error }) => (
  <ProductGrid
    products={products}
    loading={loading}
    error={error}
    emptyMessage="No featured fragrances available"
    emptyDescription="Discover our curated selection of premium fragrances."
    className="mb-12"
  />
);

export const RelatedProductGrid = ({ products, loading, error }) => (
  <ProductGrid
    products={products}
    loading={loading}
    error={error}
    emptyMessage="No related fragrances found"
    emptyDescription="Explore our complete collection for more exquisite options."
    className="mb-12"
  />
);

export const CompactProductGrid = ({ products, loading, error }) => (
  <ProductGrid
    products={products}
    loading={loading}
    error={error}
    emptyMessage="No products found"
    emptyDescription="Refine your search to discover more."
    className="mb-8"
  />
);

export default ProductGrid;